import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"

import { useUsableAvatarSrc } from "@/hooks/use-usable-avatar-image"
import { cn } from "@/lib/utils"

const Avatar = React.forwardRef(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)}
    {...props} />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

// A placeholder photo (a 1x1 PNG, which is what anonymised profiles store) loads like any other
// image, so Radix would call it "loaded" and hide <AvatarFallback> behind one stretched pixel.
// useUsableAvatarSrc measures the bitmap and blanks the src for anything that is not a real
// picture — with no src Radix reports "error", and the initials fallback renders instead.
const AvatarImage = React.forwardRef(({ className, src, ...props }, ref) => {
  const usableSrc = useUsableAvatarSrc(src)
  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn("aspect-square h-full w-full", className)}
      src={usableSrc || undefined}
      {...props} />
  )
})
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    )}
    {...props} />
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
